import { motion } from 'framer-motion'
import { ListChecks } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { AIRecommendation } from '../../types'
import { BRAND } from '../../config'
import { ReportSectionCard, ReportSectionHeader, ReportSectionBody } from './ReportSectionCard'

function impactBadgeClass(level: string): string {
  switch (level) {
    case 'high':
      return 'bg-[#ffe2e2] text-[#c10007]'
    case 'medium':
      return 'bg-[#fef3c6] text-[#a65f00]'
    default:
      return 'bg-[#dcfce7] text-[#008236]'
  }
}

function effortBadgeClass(level: string): string {
  switch (level) {
    case 'high':
      return 'bg-[#f3f4f6] text-[#6a7282]'
    case 'medium':
      return 'bg-[#dbeafe] text-[#1447e6]'
    default:
      return 'bg-[#dcfce7] text-[#008236]'
  }
}

function impactAccent(level: string): string {
  switch (level) {
    case 'high':
      return BRAND.red
    case 'medium':
      return '#d08700'
    default:
      return '#00a63e'
  }
}

interface RecommendationsSectionProps {
  recommendations: AIRecommendation[]
}

export function RecommendationsSection({ recommendations }: RecommendationsSectionProps) {
  const { t } = useTranslation('report')
  const items = recommendations.slice(0, 3)
  if (items.length === 0) return null

  return (
    <ReportSectionCard delay={0.15}>
      <ReportSectionHeader
        icon={ListChecks}
        accentColor="#2563eb"
        title={t('recommendations.title')}
        titleId="recommendations-heading"
      />

      <ReportSectionBody className="space-y-3">
        {items.map((rec, i) => (
          <motion.article
            key={`${rec.priority}-${rec.title}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 + i * 0.06 }}
            className="relative overflow-hidden rounded-[14px] bg-[#f2f2f6] px-4 py-4 ps-5"
          >
            <span
              className="absolute inset-y-3 inset-s-0 w-[3px] rounded-full"
              style={{ backgroundColor: impactAccent(rec.impact) }}
              aria-hidden
            />

            <div className="flex flex-wrap items-start justify-between gap-3 gap-y-2">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <div
                  className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                  style={{ backgroundColor: BRAND.navy }}
                >
                  {rec.priority}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold leading-5 text-[#1e2939]">{rec.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-[#6a7282]">{rec.description}</p>
                </div>
              </div>

              <div className="flex w-full shrink-0 flex-wrap items-center gap-1.5 ps-9 sm:w-auto sm:ps-0">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${impactBadgeClass(rec.impact)}`}
                >
                  {t('recommendations.impact', { level: rec.impact })}
                </span>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${effortBadgeClass(rec.effort)}`}
                >
                  {t('recommendations.effort', { level: rec.effort })}
                </span>
              </div>
            </div>
          </motion.article>
        ))}
      </ReportSectionBody>
    </ReportSectionCard>
  )
}
